import { useQueryClient } from '@tanstack/react-query'
import { Camera, ImagePlus, Plus, X } from 'lucide-react'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { shapeOf, tenseOf, today } from '../../shared/momentStyle'
import type { ToldAs } from '../../shared/telling'
import { showDate } from '../../shared/tree'
import { Choice } from '@/components/Choice'
import { DatePicker } from '@/components/DatePicker'
import { FormError } from '@/components/FormError'
import { LiveDot, ShapeChoice, TenseChoice } from '@/components/MomentStyle'
import { PersonSelect } from '@/components/PersonSelect'
import { ShareWith } from '@/components/ShareWith'
import { Avatar } from '@/components/tree/Avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { api } from '@/lib/api'
import { useFamily, type Family } from '@/lib/families'
import { usePageFrame } from '@/lib/frame'
import {
  momentPhotoUrl,
  paragraphs,
  PHOTOS_PER_MOMENT,
  tellStory,
  uploadMomentPhoto,
  useMoment,
  type Moment,
  type MomentFields,
  type Told,
} from '@/lib/moments'
import { audienceFrom, ONLY_ME, useLastShare } from '@/lib/spaces'
import { PHOTO_TYPES, preparePhoto, type PreparedPhoto } from '@/lib/photos'
import { useTree } from '@/lib/tree'
import { useMe } from '@/lib/useMe'
import { WithFamily } from './FamilyPage'
import { PageLoader } from '@/components/Loader'

export const NewMoment = () => <WithFamily>{(family) => <MomentForm family={family} />}</WithFamily>

export function EditMoment() {
  const { id = '' } = useParams()
  const moment = useMoment(id)
  const family = useFamily(moment.data?.familyId ?? '', { enabled: !!moment.data })
  if (moment.isError) return <FormError error={moment.error} />
  if (family.isError) return <FormError error={family.error} />
  if (!moment.data || !family.data) return <PageLoader />
  if (!moment.data.mine) return <FormError error="Only the person who wrote this moment can change it." />
  return <MomentForm family={family.data} moment={moment.data} />
}

type NewPhoto = { key: string; prepared: PreparedPhoto; preview: string; uploaded: boolean }

// Writing a moment, or changing one: what it's called, when it happened (as roughly as you know), the story, who was
// there, a few photos, and who sees it. A new moment starts with whoever you shared your last new thing with (just you,
// the first time); Dallas chose that over private by default on 2026-10-17. One started from
// someone's page ("Write a moment with…", or "Write a memory" on a memorial page; ?with=<person>) starts with them
// tagged. Once the story's written, it can be left as written, polished or retold for everyone else to read; the
// author always keeps, and edits, the story as they wrote it.
function MomentForm({ family, moment }: { family: Family; moment?: Moment }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [search] = useSearchParams()
  const about = search.get('with')
  const listTo = `/circle/${family.id}/moments`
  const aboutName = useTree(family.id).data?.people.find((p) => p.id === about)?.name
  usePageFrame(family.id, {
    title: moment ? 'Edit moment' : 'Write a moment',
    back: moment
      ? { to: `/moment/${moment.id}`, label: `Back to ${moment.title}` }
      : about
        ? { to: `/person/${about}`, label: `Back to ${aboutName ?? 'their page'}` }
        : { to: listTo, label: 'Back to moments' },
  })
  const written = moment?.written ?? moment?.body ?? ''
  const lastShare = useLastShare()
  const [fields, setFields] = useState<MomentFields>({
    title: moment?.title ?? '',
    body: written,
    happenedOn: moment?.happenedOn ?? null,
    ...(moment ? audienceFrom(moment) : lastShare.start(ONLY_ME)),
    people: moment?.people.map((x) => x.id) ?? (about ? [about] : []),
    told: moment?.toldAs ? { as: moment.toldAs, text: moment.body } : null,
    // A new moment is a memory unless it was started as happening now (?now=1).
    tense: moment ? tenseOf(moment) : search.get('now') === '1' ? 'now' : 'then',
    shape: moment?.shape ?? null,
  })
  // A Now moment is dated to the day it was written: today for a new one, or the day an older one was first saved.
  const nowDay = moment ? today(new Date(moment.createdAt)) : today()
  const telling = useTelling(family.id, written, fields.told)
  const [savedId, setSavedId] = useState(moment?.id)
  const [photos, setPhotos] = useState<NewPhoto[]>([])
  const [removed, setRemoved] = useState<string[]>([])
  const [status, setStatus] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const camera = useRef<HTMLInputElement>(null)
  const library = useRef<HTMLInputElement>(null)

  // Previews are local object URLs; free them when this page closes.
  const previews = useRef<string[]>([])
  useEffect(() => () => previews.current.forEach((url) => URL.revokeObjectURL(url)), [])

  const kept = (moment?.photos ?? []).filter((p) => !removed.includes(p.id))
  const room = PHOTOS_PER_MOMENT - kept.length - photos.length

  async function addFiles(files: FileList | null) {
    setError('')
    const picked = Array.from(files ?? [])
    if (picked.length > room) {
      setError(`A moment can hold up to ${PHOTOS_PER_MOMENT} photos. An album can hold the rest.`)
    }
    for (const file of picked.slice(0, Math.max(room, 0))) {
      try {
        const prepared = await preparePhoto(file, { medium: true })
        const preview = URL.createObjectURL(prepared.thumb)
        previews.current.push(preview)
        setPhotos((list) => [...list, { key: preview, prepared, preview, uploaded: false }])
      } catch (e) {
        setError((e as Error).message)
      }
    }
  }

  async function save(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      const story = fields.body.trim()
      // A version made from an earlier draft of the story is made again from this one first.
      const told = telling.pick === 'written' || !story ? null : await telling.ensure(telling.pick, story)
      const happenedOn = fields.tense === 'now' ? nowDay : fields.happenedOn
      const body = { ...fields, title: fields.title.trim(), body: story, told, happenedOn }
      let id = savedId
      if (id) {
        await api(`/moments/${id}`, { method: 'PATCH', body })
      } else {
        setStatus('Saving the moment…')
        id = (await api<{ id: string }>(`/families/${family.id}/moments`, { method: 'POST', body })).id
        setSavedId(id)
        lastShare.remember(fields)
      }
      for (const photoId of removed) await api(`/moment-photos/${photoId}`, { method: 'DELETE' })
      setRemoved([])
      const pending = photos.filter((p) => !p.uploaded)
      for (const [i, photo] of pending.entries()) {
        setStatus(`Uploading photo ${i + 1} of ${pending.length}…`)
        await uploadMomentPhoto(id, photo.prepared)
        setPhotos((list) => list.map((p) => (p.key === photo.key ? { ...p, uploaded: true } : p)))
      }
      await queryClient.invalidateQueries({ queryKey: ['moment', id] })
      // Every circle's list: it may be shared into others too.
      await queryClient.invalidateQueries({ queryKey: ['moments'] })
      // The pages of whoever it tags list it.
      await queryClient.invalidateQueries({ queryKey: ['person'] })
      navigate(`/moment/${id}`, { replace: true })
    } catch (e) {
      // Whatever already saved stays saved; saving again picks up where this stopped.
      setError(
        e instanceof TellingError
          ? `${e.message} Choose “As written” to save it as it is.`
          : `${(e as Error).message} Your moment is kept here; tap Save to try again.`,
      )
      setStatus('')
      setBusy(false)
    }
  }

  const shown = [
    ...kept.map((p, i) => ({ key: p.id, src: momentPhotoUrl(p.id, 'thumb'), name: `Photo ${i + 1}`, kept: true })),
    ...photos.map((p, i) => ({ key: p.key, src: p.preview, name: `New photo ${i + 1}`, kept: false })),
  ]
  function remove(item: (typeof shown)[number]) {
    if (item.kept) setRemoved((r) => [...r, item.key])
    else setPhotos((list) => list.filter((p) => p.key !== item.key))
  }

  return (
    <form onSubmit={save} className="flex flex-col gap-8">
      <header className="flex flex-col gap-1.5">
        <Link
          to={moment ? `/moment/${moment.id}` : listTo}
          className="self-start eyebrow hover:underline max-nav:hidden"
        >
          ← {moment ? moment.title : 'Moments'}
        </Link>
        <h1 className="text-display">{moment ? 'Edit moment' : 'Write a moment'}</h1>
        {!moment && <p className="mt-1 whisper">Tell it the way you’d tell it at the table.</p>}
      </header>

      <TenseChoice value={fields.tense} onChange={(tense) => setFields((f) => ({ ...f, tense }))} />

      <div className="grid gap-2">
        <Label htmlFor="moment-title">What’s it called?</Label>
        <Input
          id="moment-title"
          required
          maxLength={120}
          value={fields.title}
          onChange={(e) => setFields((f) => ({ ...f, title: e.target.value }))}
        />
      </div>

      {fields.tense === 'now' ? (
        <div className="grid gap-2">
          <p className="text-sm font-medium">When</p>
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <LiveDot />
            <span>{showDate(nowDay)}</span>
            <span className="text-sub text-muted-foreground">Kept as written at the time.</span>
          </p>
        </div>
      ) : (
        <div className="grid gap-2">
          <Label htmlFor="moment-when">When was it?</Label>
          <DatePicker
            id="moment-when"
            placeholder="Not sure"
            aria-describedby="moment-when-hint"
            value={fields.happenedOn}
            onChange={(happenedOn) => setFields((f) => ({ ...f, happenedOn }))}
          />
          <p id="moment-when-hint" className="text-sub text-muted-foreground">
            Just the year is fine. Leave it if you’re not sure.
          </p>
        </div>
      )}

      <div className="grid gap-2">
        <Label htmlFor="moment-story">The story</Label>
        <Textarea
          id="moment-story"
          rows={9}
          maxLength={10_000}
          value={fields.body}
          onChange={(e) => setFields((f) => ({ ...f, body: e.target.value }))}
        />
      </div>

      <HowItReads story={fields.body} telling={telling} />

      <WhoWasThere
        familyId={family.id}
        value={fields.people}
        onChange={(people) => setFields((f) => ({ ...f, people }))}
      />

      <section
        className="flex flex-col gap-4"
        aria-labelledby="moment-photos"
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes('Files')) e.preventDefault()
        }}
        onDrop={(e) => {
          if (!e.dataTransfer.types.includes('Files')) return
          e.preventDefault()
          void addFiles(e.dataTransfer.files)
        }}
      >
        <div className="flex flex-col gap-1">
          <h2 id="moment-photos" className="text-title">
            Photos
          </h2>
          <p className="text-sub text-muted-foreground">
            Optional. Up to {PHOTOS_PER_MOMENT}; the first leads the moment. The originals are kept exactly as taken.
          </p>
        </div>
        {shown.length > 0 && (
          <ul className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 sm:gap-2">
            {shown.map((item) => (
              <li key={item.key} className="relative">
                <img src={item.src} alt={item.name} className="aspect-square w-full rounded-xl bg-muted object-cover" />
                {!busy && (
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="absolute top-1.5 right-1.5 size-8 rounded-full bg-background/90 shadow-paper"
                    aria-label={`Remove ${item.name.toLowerCase()}`}
                    onClick={() => remove(item)}
                  >
                    <X aria-hidden />
                  </Button>
                )}
              </li>
            ))}
            {room > 0 && (
              <li>
                <button
                  type="button"
                  onClick={() => library.current?.click()}
                  className="group grid aspect-square w-full place-items-center rounded-xl bg-secondary transition-colors outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50"
                >
                  <span className="grid size-10 place-items-center rounded-full bg-background shadow-paper transition-transform group-hover:scale-105">
                    <Plus className="size-5" aria-hidden />
                  </span>
                  <span className="sr-only">Add another photo</span>
                </button>
              </li>
            )}
          </ul>
        )}
        {room > 0 && (
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="lg" variant="outline" onClick={() => camera.current?.click()}>
              <Camera aria-hidden /> Take a photo
            </Button>
            <Button type="button" size="lg" variant="outline" onClick={() => library.current?.click()}>
              <ImagePlus aria-hidden /> Choose photos
            </Button>
          </div>
        )}
        <input
          ref={camera}
          type="file"
          accept={PHOTO_TYPES}
          capture="environment"
          className="hidden"
          aria-hidden
          tabIndex={-1}
          onChange={(e) => {
            void addFiles(e.target.files)
            e.target.value = ''
          }}
        />
        <input
          ref={library}
          data-testid="choose-moment-photos"
          type="file"
          accept={PHOTO_TYPES}
          multiple
          className="hidden"
          aria-hidden
          tabIndex={-1}
          onChange={(e) => {
            void addFiles(e.target.files)
            e.target.value = ''
          }}
        />
      </section>

      <ShapeChoice
        value={shapeOf({ shape: fields.shape, photoCount: shown.length, body: fields.body })}
        onChange={(shape) => setFields((f) => ({ ...f, shape }))}
        cover={shown[0]?.src}
        hasPhotos={shown.length > 0}
      />

      <section className="flex flex-col gap-4" aria-labelledby="moment-who">
        <div className="flex flex-col gap-1">
          <h2 id="moment-who" className="text-title">
            Who sees it?
          </h2>
          <p className="text-sub text-muted-foreground">You can change this any time.</p>
        </div>
        <ShareWith
          here={family.id}
          value={fields}
          onChange={(who) => setFields((f) => ({ ...f, ...who }))}
          onlyMeHint="Kept just for you. Share it whenever you’re ready."
        />
      </section>

      <FormError error={error} />
      {status && (
        <p role="status" className="text-sm text-muted-foreground">
          {status}
        </p>
      )}
      {/* On a phone the way forward stays at the bottom of the screen, under the thumb, as the form scrolls. */}
      <div className="flex flex-row-reverse flex-wrap justify-between gap-2 max-nav:sticky max-nav:bottom-0 max-nav:z-10 max-nav:-mx-5 max-nav:bg-background/90 max-nav:px-5 max-nav:pt-3 max-nav:pb-[calc(var(--safe-bottom)+0.5rem)] max-nav:backdrop-blur-lg sm:max-nav:-mx-8 sm:max-nav:px-8">
        <Button type="submit" size="lg" disabled={busy} className="max-nav:min-w-36 max-nav:flex-1">
          {busy ? 'Saving…' : 'Save moment'}
        </Button>
      </div>
    </form>
  )
}

// Who was there: people from the circle's tree, each picked one at a time. Tagging someone puts the moment on their page
// for the people who can already see it; it never shares it with anyone new.
function WhoWasThere({
  familyId,
  value,
  onChange,
}: {
  familyId: string
  value: string[]
  onChange: (people: string[]) => void
}) {
  const tree = useTree(familyId)
  const everyone = tree.data?.people ?? []
  const tagged = value.flatMap((id) => everyone.filter((p) => p.id === id))
  const others = everyone.filter((p) => !value.includes(p.id))
  // Someone asked for in the link (?with=) who isn't on this tree is left out.
  const stray = !!tree.data && tagged.length < value.length
  useEffect(() => {
    if (stray) onChange(tagged.map((p) => p.id))
  }, [stray, tagged, onChange])
  return (
    <section className="flex flex-col gap-4" aria-labelledby="moment-people">
      <div className="flex flex-col gap-1">
        <h2 id="moment-people" className="text-title">
          Who was there?
        </h2>
        <p className="text-sub text-muted-foreground">
          Optional. The moment shows on their page, only for the people you share it with.
        </p>
      </div>
      {tagged.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Tagged in this moment">
          {tagged.map((p) => (
            <li
              key={p.id}
              className="flex min-h-10 items-center gap-2 rounded-full bg-secondary pl-1 text-sm font-medium"
            >
              <Avatar person={p} className="size-8 text-sm shadow-none" />
              {p.name}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-9"
                aria-label={`Untag ${p.name}`}
                onClick={() => onChange(value.filter((id) => id !== p.id))}
              >
                <X aria-hidden />
              </Button>
            </li>
          ))}
        </ul>
      )}
      {others.length > 0 && (
        <PersonSelect
          aria-label="Tag someone who was there"
          placeholder="Tag someone"
          people={others}
          value=""
          onChange={(id) => id && onChange([...value, id])}
        />
      )}
    </section>
  )
}

type Pick = 'written' | ToldAs
type Version = Told & { from: string }
class TellingError extends Error {}

// The story's polished and retold versions as they're made, each remembering the words it was made from, so changing
// the story never leaves an old version behind to be saved by mistake.
function useTelling(familyId: string, written: string, saved: Told | null) {
  const [pick, setPick] = useState<Pick>(saved?.as ?? 'written')
  const [versions, setVersions] = useState<Version[]>(saved ? [{ ...saved, from: written.trim() }] : [])
  const [busy, setBusy] = useState<ToldAs | null>(null)
  const [error, setError] = useState('')
  const versionOf = (as: ToldAs, story: string) => versions.find((v) => v.as === as && v.from === story) ?? null

  async function ensure(as: ToldAs, story: string): Promise<Told> {
    const have = versionOf(as, story)
    if (have) return { as, text: have.text }
    setBusy(as)
    setError('')
    try {
      const made = await tellStory(familyId, story, as)
      setVersions((list) => [...list.filter((v) => v.as !== as), { ...made, from: story }])
      return made
    } catch (e) {
      setError((e as Error).message)
      throw new TellingError((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  function choose(next: Pick, story: string) {
    setError('')
    setPick(next)
    if (next !== 'written' && story) ensure(next, story).catch(() => setPick('written'))
  }

  return { pick, choose, ensure, versionOf, busy, error }
}

const HOW: [Pick, string][] = [
  ['written', 'As written'],
  ['polished', 'Polished'],
  ['retold', 'Retold'],
]
const HINT: Record<Pick, string> = {
  written: 'Everyone reads it exactly as you wrote it.',
  polished: 'Punctuation, spelling and paragraphs tidied. Every word is yours, in your order.',
  retold: 'A little easier to read, the way an editor would leave it. Still your words and your voice.',
}

// How everyone else reads the story: as written, polished or retold. The version shows here before it's saved, and the
// story as written is always kept for its author to come back to.
function HowItReads({ story, telling }: { story: string; telling: ReturnType<typeof useTelling> }) {
  const me = useMe()
  const text = story.trim()
  if (me.data?.telling !== true || !text) return null
  const { pick, busy, error } = telling
  const version = pick === 'written' ? null : telling.versionOf(pick, text)
  const stale = pick !== 'written' && !version && !busy && !error
  return (
    <section className="flex flex-col gap-4" aria-labelledby="moment-how">
      <div className="flex flex-col gap-1">
        <h2 id="moment-how" className="text-title">
          How should it read?
        </h2>
        <p className="text-sub text-muted-foreground">
          Only you will see what you wrote, and you can change it any time.
        </p>
      </div>
      <Choice
        legend="How others read your story"
        hint={HINT[pick]}
        value={pick}
        options={HOW}
        onChange={(v) => telling.choose(v as Pick, text)}
      />
      {busy ? (
        <p role="status" className="text-sm text-muted-foreground">
          {busy === 'polished' ? 'Polishing your words…' : 'Retelling your words…'}
        </p>
      ) : error ? (
        <p role="status" className="text-sm text-muted-foreground">
          {error}
        </p>
      ) : stale ? (
        <p className="text-sm text-muted-foreground">
          Your story changed since.{' '}
          <button
            type="button"
            className="font-medium text-brand underline underline-offset-4"
            onClick={() => telling.choose(pick, text)}
          >
            {pick === 'polished' ? 'Polish it again' : 'Retell it again'}
          </button>
        </p>
      ) : null}
      {version && (
        <div
          aria-label={pick === 'polished' ? 'Your story, polished' : 'Your story, retold'}
          className="flex flex-col gap-3 rounded-2xl bg-secondary px-4 py-3 text-body leading-relaxed"
        >
          {paragraphs(version.text).map((p, i) => (
            <p key={i} className="whitespace-pre-line">
              {p}
            </p>
          ))}
        </div>
      )}
    </section>
  )
}
